import { useState, useRef, useEffect, forwardRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { buildDropShadowFilter } from "../../utils/dropShadowFilter";
import { generateOutlineDataUrl } from "../../utils/generateOutlineImage";
import {
  clampPositionAllowOverflow,
  subjectImageFrameStyle,
  subjectCropClipPath,
} from "../../components/objectTransform/objectTransformMath";
import {
  getImageLayers,
  normalizeLayers,
  useEditorStore,
} from "../../state/editorStore";
import { computeFittedCanvasSize } from "./computeFittedCanvasSize";
import "./CanvasEditor.css";
import { useCanvasCommands } from "./useCanvasCommands";
import { useSubjectImageLoad } from "./useSubjectImageLoad";
import { useCanvasGestures } from "./useCanvasGestures";
import { CanvasStageView } from "./CanvasStageView";

const CanvasEditor = forwardRef(({ imageUrl, creationId = null, backgroundColor = "#ffffff", backgroundLayerVisible = true, objectLayerVisible = true, onCanvasReady, onSubjectReady, onNaturalSize = null, onCanvasClick, onObjectClick, onOverlayClick, onWorkspaceMarginClick, onObjectTransformChange, isCanvasPreloading = false, resolutionLabel = "", lockObjectTransform = false, softOverflow = false }, ref) => {
  const { getToken } = useAuth();
  const containerRef = useRef(null);
  const layers = useEditorStore((s) => s.layers);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const updateImageLayer = useEditorStore((s) => s.updateImageLayer);
  const pushSnapshot = useEditorStore((s) => s.pushSnapshot);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const watermarkState = useEditorStore((s) => s.object?.watermark);
  const normalizedLayers = normalizeLayers(layers);
  const imageOverlays = getImageLayers(normalizedLayers);
  const objectLayerMeta = normalizedLayers.find((l) => l.id === "object");
  const objectZIndex = (objectLayerMeta?.zIndex ?? 1) + 10;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);

  // Canvas state
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [canvasCornerRadius, setCanvasCornerRadius] = useState(0);
  const [objectCornerRadii, setObjectCornerRadii] = useState({
    tl: 0,
    tr: 0,
    br: 0,
    bl: 0,
  });

  // Object state (foreground image)
  // MEASUREMENT FIX: objectSize represents ONLY the image content dimensions
  // It excludes resize handles, rotation controls, selection borders, and any padding.
  // All fit/clamp/bound logic must use objectSize (never wrapper's visual bounds).
  const [objectSize, setObjectSize] = useState({ width: 400, height: 300 });
  const [objectPosition, setObjectPosition] = useState({ x: 200, y: 150 });
  const [objectRotation, setObjectRotation] = useState(0); // Rotation angle in degrees
  const [objectFlip, setObjectFlip] = useState({ x: 1, y: 1 });

  // Selection state
  const [selectedElement, setSelectedElement] = useState(null); // "canvas" or "object"

  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [objectHovered, setObjectHovered] = useState(false);
  const [alignGuides, setAlignGuides] = useState({ vertical: [], horizontal: [] });
  const alignGuideKeyRef = useRef("|");
  const alignOthersRef = useRef([]);
  const snapMovingFrameRef = useRef(null);
  const clearAlignGuidesRef = useRef(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const wasObjectTransformedRef = useRef(false);
  // Interaction guard: prevents canvas click after object transform
  const [wasObjectTransformed, setWasObjectTransformed] = useState(false);
  wasObjectTransformedRef.current = wasObjectTransformed;

  // Crop state for side handle cropping
  const [originalImageSize, setOriginalImageSize] = useState({ width: 0, height: 0 });
  const [cropState, setCropState] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });

  // Shadow state (isolated, opt-in feature)
  const [shadowState, setShadowState] = useState({
    enabled: false,
    offsetX: 0,
    offsetY: 0,
    blur: 0,
    opacity: 0.4,
    color: "#000000"
  });

  // Outline state (isolated, opt-in feature)
  const [outlineState, setOutlineState] = useState({
    enabled: false,
    width: 0,
    blur: 0,
    color: "#000000"
  });

  // Outline image data URL (generated bitmap)
  const [outlineImageUrl, setOutlineImageUrl] = useState(null);
  /** Native-px padding baked into outlineImageUrl so stroke can extend past BB. */
  const [outlineBleedPx, setOutlineBleedPx] = useState(0);

  // Background image state (locked, non-interactive layer)
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);
  const [backgroundImageLoading, setBackgroundImageLoading] = useState(false);
  /** Short hold so the overlay does not flash on instant state flips. */
  const MIN_OVERLAY_MS = 280;
  const isCanvasProcessingActive = isCanvasPreloading || backgroundImageLoading;
  const [showCanvasProcessingOverlay, setShowCanvasProcessingOverlay] = useState(false);
  const [alphaMaskReady, setAlphaMaskReady] = useState(false);

  const canvasRef = useRef(null);
  const canvasSizeRef = useRef({ width: 0, height: 0 });
  const imageLoadedRef = useRef(false);
  const canvasReadyRef = useRef(false);
  const subjectReadyNotifiedUrlRef = useRef(null);
  const onSubjectReadyRef = useRef(onSubjectReady);
  onSubjectReadyRef.current = onSubjectReady;
  const alphaMaskRef = useRef(null);
  const alphaMaskFallbackRef = useRef(false);
  const objectTightBoundsRef = useRef(false);
  const layoutLockRef = useRef(false);
  /** Full-frame FILL/FIT object geometry while layout is locked (before subject retighten). */
  const layoutLockFullObjectRef = useRef(null);
  const cropStateRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  /** Largest crop after background removal. Edge handles cannot expand past this. */
  const cropExtentRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const originalImageSizeRef = useRef({ width: 0, height: 0 });
  const objectSizeRef = useRef({ width: 400, height: 300 });
  const objectPositionRef = useRef({ x: 200, y: 150 });
  const objectRotationRef = useRef(0);
  const objectFlipRef = useRef({ x: 1, y: 1 });
  /** Active rotate gesture — refs avoid stale closures / listener rebinds during drag. */
  const rotateSessionRef = useRef(null);
  const rotateRafRef = useRef(null);
  const resizeSessionRef = useRef(null);
  const transformRafRef = useRef(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const onObjectClickRef = useRef(onObjectClick);
  onObjectClickRef.current = onObjectClick;
  const onObjectTransformChangeRef = useRef(onObjectTransformChange);
  onObjectTransformChangeRef.current = onObjectTransformChange;
  const autoSelectedForUrlRef = useRef(null);
  /**
   * AI drop-shadow: keep current canvas/object geometry on the next image swap,
   * tight opaque crop for the BB, but allow soft shadow to paint outside (no clipPath).
   */
  const preserveLayoutPendingRef = useRef(false);
  const preserveLayoutSnapshotRef = useRef(null);
  /** Tool-prep bounding box (fractions of the stage) to apply once the canvas exists. */
  const pendingSubjectPlacementRef = useRef(null);
  /** Next image load: fill canvas with full bitmap (Remove BG toggle OFF). */
  const forceFullFramePendingRef = useRef(false);
  const allowSoftOverflowForUrlRef = useRef(null);
  /** Full bitmap is the object (no opaque crop). Set for a shadow opened with no prior layout. */
  const fullFrameShadowRef = useRef(null);
  const [softOverflowEnabled, setSoftOverflowEnabled] = useState(false);

  /**
   * Under tool-prep FILL/FIT lock the object covers the canvas. Once we have a
   * cutout alpha mask, shrink the wrapper to the opaque subject while keeping
   * the locked canvas AR and the same image→screen scale.
   */
  const notifyObjectTransform = () => {
    const flip = objectFlipRef.current;
    onObjectTransformChangeRef.current?.({
      rotation: objectRotationRef.current,
      flipH: flip.x < 0,
      flipV: flip.y < 0,
    });
  };

  useEffect(() => {
    canvasSizeRef.current = canvasSize;
  }, [canvasSize]);

  useEffect(() => {
    imageLoadedRef.current = imageLoaded;
  }, [imageLoaded]);

  useEffect(() => {
    canvasReadyRef.current = canvasReady;
  }, [canvasReady]);

  useEffect(() => {
    cropStateRef.current = cropState;
  }, [cropState]);

  useEffect(() => {
    originalImageSizeRef.current = originalImageSize;
  }, [originalImageSize]);

  useEffect(() => {
    objectSizeRef.current = objectSize;
  }, [objectSize]);

  useEffect(() => {
    objectPositionRef.current = objectPosition;
  }, [objectPosition]);

  useEffect(() => {
    objectRotationRef.current = objectRotation;
  }, [objectRotation]);

  useEffect(() => {
    objectFlipRef.current = objectFlip;
  }, [objectFlip]);
  const backgroundCanvasRef = useRef(null);
  const outlineRenderTokenRef = useRef(0);
  const outlineDebounceTimerRef = useRef(null);
  const outlineProcessingRef = useRef(false);
  const scanOverlayStartedAtRef = useRef(0);
  const scanOverlayHideTimerRef = useRef(null);

  useEffect(() => {
    if (isCanvasProcessingActive) {
      if (scanOverlayHideTimerRef.current) {
        clearTimeout(scanOverlayHideTimerRef.current);
        scanOverlayHideTimerRef.current = null;
      }

      if (!showCanvasProcessingOverlay) {
        scanOverlayStartedAtRef.current = Date.now();
        setShowCanvasProcessingOverlay(true);
      }
      return;
    }

    if (!showCanvasProcessingOverlay) return;

    const elapsed = Date.now() - scanOverlayStartedAtRef.current;
    const remaining = Math.max(0, MIN_OVERLAY_MS - elapsed);

    if (remaining === 0) {
      setShowCanvasProcessingOverlay(false);
      scanOverlayStartedAtRef.current = 0;
      return;
    }

    scanOverlayHideTimerRef.current = setTimeout(() => {
      setShowCanvasProcessingOverlay(false);
      scanOverlayStartedAtRef.current = 0;
      scanOverlayHideTimerRef.current = null;
    }, remaining);

    return () => {
      if (scanOverlayHideTimerRef.current) {
        clearTimeout(scanOverlayHideTimerRef.current);
        scanOverlayHideTimerRef.current = null;
      }
    };
  }, [isCanvasProcessingActive, showCanvasProcessingOverlay]);

  useEffect(() => {
    return () => {
      if (scanOverlayHideTimerRef.current) {
        clearTimeout(scanOverlayHideTimerRef.current);
      }
    };
  }, []);

  // Reflow canvas when the editor workspace is resized (e.g. dragging tab between monitors).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let frameId = null;

    const fitCanvasToContainer = () => {
      const prevCanvas = canvasSizeRef.current;
      const prevObjectSize = objectSizeRef.current;
      const prevObjectPosition = objectPositionRef.current;

      if (!imageLoadedRef.current || !prevCanvas.width || !prevCanvas.height) {
        return;
      }

      const aspectRatio = prevCanvas.width / prevCanvas.height;
      const nextCanvas = computeFittedCanvasSize(
        container.clientWidth,
        container.clientHeight,
        aspectRatio
      );
      if (!nextCanvas) return;

      if (
        Math.abs(nextCanvas.width - prevCanvas.width) < 2 &&
        Math.abs(nextCanvas.height - prevCanvas.height) < 2
      ) {
        return;
      }

      const scaleX = nextCanvas.width / prevCanvas.width;
      const scaleY = nextCanvas.height / prevCanvas.height;

      let newImgWidth = prevObjectSize.width * scaleX;
      let newImgHeight = prevObjectSize.height * scaleY;

      if (layoutLockFullObjectRef.current) {
        const b = layoutLockFullObjectRef.current;
        layoutLockFullObjectRef.current = {
          ...b,
          width: b.width * scaleX,
          height: b.height * scaleY,
          x: b.x * scaleX,
          y: b.y * scaleY,
        };
      }

      // When layout is locked (tool-prep FILL/FIT), keep cover overflow — do not shrink back to contain.
      if (
        !layoutLockRef.current &&
        (newImgWidth > nextCanvas.width || newImgHeight > nextCanvas.height)
      ) {
        const scale = Math.min(
          nextCanvas.width / newImgWidth,
          nextCanvas.height / newImgHeight
        );
        newImgWidth *= scale;
        newImgHeight *= scale;
      }

      setCanvasSize(nextCanvas);
      setObjectSize({ width: newImgWidth, height: newImgHeight });
      setObjectPosition(() => {
        if (layoutLockRef.current) {
          return {
            x: prevObjectPosition.x * scaleX,
            y: prevObjectPosition.y * scaleY,
          };
        }
        return clampPositionAllowOverflow(
          prevObjectPosition.x * scaleX,
          prevObjectPosition.y * scaleY,
          newImgWidth,
          newImgHeight,
          nextCanvas.width,
          nextCanvas.height
        );
      });
    };

    const observer = new ResizeObserver(() => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        fitCanvasToContainer();
      });
    });

    observer.observe(container);
    fitCanvasToContainer();
    return () => {
      observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [imageUrl]);

  useCanvasCommands(ref, {
    backgroundImageUrl,
    canvasSize,
    objectSize,
    outlineBleedPx,
    outlineImageUrl,
    outlineState,
    selectedElement,
    setBackgroundImageLoading,
    setBackgroundImageUrl,
    setCanvasCornerRadius,
    setCanvasSize,
    setCropState,
    setIsDragging,
    setIsResizing,
    setIsRotating,
    setObjectCornerRadii,
    setObjectFlip,
    setObjectPosition,
    setObjectRotation,
    setObjectSize,
    setOriginalImageSize,
    setOutlineState,
    setSelectedElement,
    setShadowState,
    shadowState,
    alphaMaskFallbackRef,
    alphaMaskRef,
    backgroundColor,
    backgroundLayerVisible,
    canvasReadyRef,
    canvasSizeRef,
    containerRef,
    cropExtentRef,
    cropStateRef,
    imageLoadedRef,
    imageUrl,
    layoutLockFullObjectRef,
    layoutLockRef,
    objectFlipRef,
    objectLayerVisible,
    objectPositionRef,
    objectRotationRef,
    objectSizeRef,
    objectTightBoundsRef,
    originalImageSizeRef,
    forceFullFramePendingRef,
    pendingSubjectPlacementRef,
    preserveLayoutPendingRef,
    preserveLayoutSnapshotRef,
    resolutionLabel,
    selectLayer,
  });
  useSubjectImageLoad({
    alphaMaskReady,
    canvasSize,
    imageLoaded,
    setAlphaMaskReady,
    setCanvasCornerRadius,
    setCanvasReady,
    setCanvasSize,
    setCropState,
    setImageLoaded,
    setObjectPosition,
    setObjectSize,
    setOriginalImageSize,
    setSelectedElement,
    setSoftOverflowEnabled,
    allowSoftOverflowForUrlRef,
    alphaMaskFallbackRef,
    alphaMaskRef,
    autoSelectedForUrlRef,
    containerRef,
    creationId,
    cropExtentRef,
    cropStateRef,
    forceFullFramePendingRef,
    fullFrameShadowRef,
    getTokenRef,
    imageUrl,
    layoutLockFullObjectRef,
    layoutLockRef,
    lockObjectTransform,
    objectPositionRef,
    objectRotationRef,
    objectSizeRef,
    objectTightBoundsRef,
    onNaturalSize,
    originalImageSizeRef,
    pendingSubjectPlacementRef,
    preserveLayoutPendingRef,
    preserveLayoutSnapshotRef,
    setObjectRotation,
    imageLoadedRef,
    softOverflow,
    subjectReadyNotifiedUrlRef,
    canvasReady,
    canvasSizeRef,
    onCanvasReady,
    onSubjectReadyRef,
  });
  const {
    handleCanvasPointerDown,
    handleCanvasClick,
    handleCanvasMouseMove,
    handleCanvasMouseLeave,
    handleResizeMouseDown,
    handleRotateMouseDown,
    snapMovingFrame,
    clearAlignGuides,
  } = useCanvasGestures({
    dragStart,
    isDragging,
    isResizing,
    isRotating,
    objectHovered,
    selectedElement,
    setAlignGuides,
    setCropState,
    setDragStart,
    setIsCropping,
    setIsDragging,
    setIsResizing,
    setIsRotating,
    setObjectHovered,
    setObjectPosition,
    setObjectRotation,
    setObjectSize,
    setSelectedElement,
    setWasObjectTransformed,
    showCanvasProcessingOverlay,
    wasObjectTransformed,
    alignGuideKeyRef,
    alignOthersRef,
    alphaMaskFallbackRef,
    alphaMaskRef,
    canvasSizeRef,
    clearAlignGuidesRef,
    cropExtentRef,
    cropStateRef,
    imageOverlays,
    imageUrl,
    lockObjectTransform,
    notifyObjectTransform,
    objectFlipRef,
    objectLayerVisible,
    objectPositionRef,
    objectRotationRef,
    objectSizeRef,
    objectTightBoundsRef,
    onCanvasClick,
    onObjectClick,
    onWorkspaceMarginClick,
    originalImageSizeRef,
    resizeSessionRef,
    rotateRafRef,
    rotateSessionRef,
    selectLayer,
    selectedLayerId,
    snapMovingFrameRef,
    transformRafRef,
    wasObjectTransformedRef,
  });
  // Helper function to build drop-shadow CSS filter from shadow state
  const getDropShadowFilter = () => buildDropShadowFilter(shadowState);

  const getSubjectImageFrameStyle = () =>
    subjectImageFrameStyle(cropState, originalImageSize);

  const getImageClipPath = (forceCropClip = false) => {
    // AI baked shadow must paint outside the opaque subject crop — clipping
    // that fringe is what made shadows look boxy at the selection box.
    if (!forceCropClip && allowSoftOverflowForUrlRef.current === imageUrl) {
      return "none";
    }
    return subjectCropClipPath(cropState, originalImageSize, objectSize);
  };

  // Generate outline image when outline settings change.
  // Bitmap is padded (bleed) so thickness is not clipped by the opaque BB / image edges.
  useEffect(() => {
    if (outlineDebounceTimerRef.current) {
      clearTimeout(outlineDebounceTimerRef.current);
      outlineDebounceTimerRef.current = null;
    }

    if (!imageUrl || !outlineState.enabled || outlineState.width === 0) {
      setOutlineImageUrl((prev) => (prev == null ? prev : null));
      setOutlineBleedPx(0);
      outlineProcessingRef.current = false;
      return;
    }

    outlineRenderTokenRef.current += 1;
    const currentToken = outlineRenderTokenRef.current;

    outlineDebounceTimerRef.current = setTimeout(() => {
      if (outlineProcessingRef.current) {
        return;
      }
      outlineProcessingRef.current = true;

      if (currentToken !== outlineRenderTokenRef.current) {
        outlineProcessingRef.current = false;
        return;
      }

      generateOutlineDataUrl(imageUrl, {
        width: outlineState.width,
        color: outlineState.color,
      })
        .then((result) => {
          if (currentToken !== outlineRenderTokenRef.current) {
            return;
          }
          if (result?.dataUrl) {
            setOutlineImageUrl(result.dataUrl);
            setOutlineBleedPx(Number(result.bleedPx) || 0);
          } else {
            setOutlineImageUrl(null);
            setOutlineBleedPx(0);
          }
        })
        .catch((error) => {
          console.error("[OUTLINE] Generation failed:", error);
          if (currentToken === outlineRenderTokenRef.current) {
            setOutlineImageUrl(null);
            setOutlineBleedPx(0);
          }
        })
        .finally(() => {
          if (currentToken === outlineRenderTokenRef.current) {
            outlineProcessingRef.current = false;
          }
        });
    }, 150);

    return () => {
      if (outlineDebounceTimerRef.current) {
        clearTimeout(outlineDebounceTimerRef.current);
      }
      outlineProcessingRef.current = false;
    };
  }, [imageUrl, outlineState.enabled, outlineState.width, outlineState.color]);

  return (
    <CanvasStageView alignGuides={alignGuides} backgroundImageUrl={backgroundImageUrl} canvasCornerRadius={canvasCornerRadius} canvasReady={canvasReady} canvasSize={canvasSize} cropState={cropState} isCropping={isCropping} isDragging={isDragging} isResizing={isResizing} isRotating={isRotating} objectCornerRadii={objectCornerRadii} objectFlip={objectFlip} objectHovered={objectHovered} objectPosition={objectPosition} objectRotation={objectRotation} objectSize={objectSize} originalImageSize={originalImageSize} outlineBleedPx={outlineBleedPx} outlineImageUrl={outlineImageUrl} outlineState={outlineState} selectedElement={selectedElement} setSelectedElement={setSelectedElement} shadowState={shadowState} showCanvasProcessingOverlay={showCanvasProcessingOverlay} backgroundCanvasRef={backgroundCanvasRef} backgroundColor={backgroundColor} backgroundLayerVisible={backgroundLayerVisible} clearAlignGuides={clearAlignGuides} containerRef={containerRef} getDropShadowFilter={getDropShadowFilter} getImageClipPath={getImageClipPath} getSubjectImageFrameStyle={getSubjectImageFrameStyle} handleCanvasClick={handleCanvasClick} handleCanvasMouseLeave={handleCanvasMouseLeave} handleCanvasMouseMove={handleCanvasMouseMove} handleCanvasPointerDown={handleCanvasPointerDown} handleResizeMouseDown={handleResizeMouseDown} handleRotateMouseDown={handleRotateMouseDown} imageOverlays={imageOverlays} imageUrl={imageUrl} lockObjectTransform={lockObjectTransform} objectLayerVisible={objectLayerVisible} objectZIndex={objectZIndex} onOverlayClick={onOverlayClick} pushSnapshot={pushSnapshot} resolutionLabel={resolutionLabel} selectLayer={selectLayer} selectedLayerId={selectedLayerId} snapMovingFrame={snapMovingFrame} updateImageLayer={updateImageLayer} watermarkState={watermarkState} />
  );
});

export default CanvasEditor;

